/*globals login, showModalError, globalNavigate */
import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import MuiTable from "common/graphs/Table/Table";
import { NumberCell } from "common/graphs/Table/TableRenders";
import { parsePoliciesIntoSets } from "../api";
import useProfile from "common/hooks/useProfile";
import { showDynPolicyRate } from "views/Config/PolicyEdit/utils";
import BqnContext from "common/BqnContext";
import { atoiv6, atoiv4, durationToInt} from "common/utils";
import { openSubscriberDetails } from "./index";

const TableContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: auto;
  margin: 0;
  padding: 0 25px;

  & > div {
    padding: 0 0 25px 0;
  }

  .muidataTable tbody td.warning {
    background: #cc0000;
    color: #fff !important;
  }

  &.colored {
    .muidataTable tbody td {
      &.warning {
         background: transparent;
         color: var(--text-primary) !important;
      }
      &.hyperlink-text {
        cursor: pointer;
      }
      &.min-q1 {
        background: #3d85c6;
        color: #fff !important;
      }
      &.q1-median {
        background: #6aa84f;
        color: #fff !important;
      }
      &.q3-max {
        background: #cc0000;
        color: #fff !important;
      }
      &.median-q3 {
        background: #e69138;
        color: #fff !important;
      }
    }
  }
`;

const returnTo = "viewStatusSubscribers";

const getPolicyType = (types, sources, name) => {
  if (sources.static.has(name) !== true) {
    return "dynamic";
  }
  if (types.rate.has(name)) {
    return "rate";
  }
  if (types.flow.has(name)) {
    return "flow";
  }
  return "monitor";
};

export const openPolicy = (name, timePeriod) =>
  ifCl
    .run("show policy")
    .then(parsePoliciesIntoSets)
    .then(({ types, sources }) => {
      const policyType = getPolicyType(types, sources, name);
      switch (policyType) {
        case "dynamic":
          return showDynPolicyRate(name);
        case "rate":
          globalNavigate("viewPolicyRateEdit", {
            returnView: returnTo,
            timePeriod,
            policy: name,
          });
          break;
        case "flow":
          globalNavigate("viewPolicyFlowEdit", {
            returnView: returnTo,
            timePeriod,
            policy: name,
          });
          break;
        case "monitor":
          globalNavigate("viewPolicyMonitorEdit", {
            returnView: returnTo,
            timePeriod,
            policy: name,
          });
          break;
        default:
      }
    });

const openPolicyOrReport = (policy, timePeriod, bqnId) =>
  openPolicy(policy, timePeriod, bqnId).catch((error) => showModalError("Error:", error));

const getWarningClass = ({threshold, value, inverted, label, limit, mean, meanValue, percent}) => {
  
  if ((mean && meanValue < mean) || label === "MEAN-Mbps") { 
    return "";
  }
  if (label === "MAX-Mbps") { 
    const percentage = (parseFloat(value) / parseFloat(limit)) * 100;
    if (parseFloat(threshold) && percentage < parseFloat(threshold)) {
      return "warning";
    }
    return "";
  }
  if(threshold === "auto"){
    if (percent !== undefined && parseFloat(value) >= percent && percent > 0) {
      return "warning";
    }
    return "";
  }
  if (inverted) {
    if (parseFloat(threshold) && parseFloat(value) > parseFloat(threshold)) {
      return "warning";
    }
    return "";
  }
  if (parseFloat(threshold) && parseFloat(value) < parseFloat(threshold)) {
    return "warning";
  }

  return "";
};

const getQuantileClass = (_value, quantiles, inverted) => {
  if (!quantiles || _value === "n/a" || _value === null) {
    return "";
  }
  const value = parseFloat(_value);
  const { q1, q3, median, min, max } = quantiles;
  if (inverted) {
    if (value > q3 && value <= max) {
      return "q3-max";
    }
    if (value > median && value <= q3) {
      return "median-q3";
    }
    if (value > q1 && value <= median) {
      return "q1-median";
    }
    if (value >= min && value <= q1) {
      return "min-q1";
    }
    return "";
  } else {
    if (value > q3 && value <= max) {
      return "min-q1";
    }
    if (value > median && value <= q3) {
      return "q1-median";
    }
    if (value > q1 && value <= median) {
      return "median-q3";
    }
    if (value >= min && value <= q1) {
      return "q3-max";
    }
    return "";
  }
};

const quartileColorCellClassName = (value, row, column) => {
  const { quantiles, label, threshold, inverted, mean, percent } = column;
  const limit = row["LIM-Mbps"];
  const meanValue = parseFloat(row["MEAN-Mbps"]);
  const warningClass = getWarningClass({
    threshold,
    value,
    inverted,
    label,
    limit,
    mean,
    meanValue,
    percent: quantiles?.pcustom
  });
  const quantileClass = getQuantileClass(value, quantiles, inverted);
  return `${warningClass} ${quantileClass}`;
};

const getColumnsToCheck = (thresholds) => {
  return [
    {
      id: "MAX-Mbps",
      label: "MAX-Mbps",
      threshold: thresholds["MAX-Mbps"],
      inverted: false,
    },
    {
      id: "RTT-P80-ms",
      label: "RTT-P80-ms",
      threshold: thresholds["RTT-80%-ms"],
      inverted: true,
    },
    {
      id: "RTT-MED-ms",
      label: "RTT-MED-ms",
      threshold: thresholds["RTT-MED-ms"],
      inverted: true,
    },
    {
      id: "RTT-MIN-ms",
      label: "RTT-MIN-ms",
      threshold: thresholds["RTT-MIN-ms"],
      inverted: true,
    },
    { id: "RTX", label: "RTX", threshold: thresholds["RTX"], inverted: true },
    {
      id: "MAX-SPEED",
      label: "MAX-SPEED-%",
      threshold: thresholds["MAX-SPEED"],
      inverted: true,
    },
    {
      id: "CONGEST",
      label: "CONGESTION",
      threshold: thresholds["CONGEST"],
      inverted: true,
    },
  ];
}

const getWarningCount = (thresholds, row, metricsQuantileData = {}) => {
  const limit = row["LIM-Mbps"];
  const meanValue = parseFloat(row["MEAN-Mbps"]);
  const mean = thresholds["MEAN-Mbps"];

  return getColumnsToCheck(thresholds).reduce(
    (count, col) =>
      getWarningClass({
        threshold: col.threshold,
        value: row[col.id],
        inverted: col.inverted,
        label: col.label,
        limit,
        mean,
        meanValue,
        percent: metricsQuantileData[col.id]?.pcustom,
      }) === "warning"
        ? count + 1
        : count,
    0,
  );
};

const formatMB = (value) => {
  if (value == null || value === "n/a") {
    return { value, precision: 0 };
  }

  const valInMB = Number(value) / 1000000;
  let precision = 0;

  if (valInMB >= 1000000) precision = 0;
  else if (valInMB >= 100000) precision = 1;
  else if (valInMB >= 10000) precision = 2;
  else if (valInMB >= 1000) precision = 3;
  else if (valInMB >= 100) precision = 4;
  else if (valInMB >= 10) precision = 5;
  else precision = 6;

  return { value: valInMB, precision };
};

const getColumns = ({
  timePeriod,
  hideRates,
  metricsQuantileData,
  thresholds,
  mode,
  loginBqnId,
  meanSorting
}) => {
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "BQN",
            title: "BQN ID",
            numeric: true,
          },
        ]
      : []),
    {
      label: "ADDR",
      id: "ADDR",
      cellClassName: "hyperlink-text",
      title: "Subscriber’s IP address",
      numeric: false,
      onClick: (addr) => openSubscriberDetails({ addr }, timePeriod),
      parse: (value) => {
        if (!value) {
          return 0;
        }
        return value.includes(":") ? atoiv6(value) : atoiv4(value);
      },
      parseFilter: (value) => value,
    },
    {
      label: "SUBSCRIBER-ID",
      id: "SUBSCRIBER-ID",
      title: "Subscriber ID",
      cellClassName: "hyperlink-text",
      onClick: (subsId) => openSubscriberDetails({ subsId }, timePeriod),
      numeric: false,
    },
    {
      label: "BLOCK",
      id: "BLOCK",
      title: login.isPlatform("gs-kr")
        ? "Blocking from quota"
        : "Blocking from billing configuration or quota",
      numeric: false,
    },
    ...(hideRates
      ? [
          {
            label: "RATE-POLICY",
            id: "POLICY-RATE",
            title: "Name of the subscriber's rate police",
            numeric: false,
            onClick: null,
            cellClassName: "hyperlink-text",
          },
        ]
      : [
          {
            label: "RATE-POLICY",
            id: "POLICY-RATE",
            title: "Name of the subscriber's rate police",
            numeric: false,
            onClick: (policy, row) => {
              const rowBqnId = row["BQN"];
              openPolicyOrReport(
                policy,
                timePeriod,
                mode === "cluster"
                  ? isNaN(rowBqnId)
                    ? loginBqnId
                    : rowBqnId
                  : null,
              );
            },
            cellClassName: "hyperlink-text",
          },
        ]),
    {
      label: "MBYTES-UP",
      id: "BYTES-UPLINK",
      title: "Total up traffic volume of this subscriber since it became active",
      numeric: true,
      renderCell: (rawValue) => {
        const { value, precision } = formatMB(rawValue);
        return <NumberCell value={value} hasNA={true} precision={precision} />;
      },
    },
    {
      label: "MBYTES-DOWN",
      id: "BYTES-DOWNLINK",
      title: "Total down traffic volume of this subscriber since it became active",
      numeric: true,
      renderCell: (rawValue) => {
        const { value, precision } = formatMB(rawValue);
        return <NumberCell value={value} hasNA={true} precision={precision} />;
      },
    },
    {
      label: "FLOWS",
      id: "FL-ACTIVE",
      title:
        "Total number of currently active traffic flows (TCP connections, UDP or IP flows)",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
      numeric: true,
    },
    {
      label: "CURR-Mbps",
      id: "CURR-Mbps",
      title: "Current speed in Mbps",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
    },
    {
      label: "MEAN-Mbps",
      id: "MEAN-Mbps",
      title:
        "Moving average of the mean speed (calculated every 10 minutes) in Mbps.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      cellClassName: quartileColorCellClassName,
      quantiles: metricsQuantileData["MEAN-Mbps"],
      threshold: thresholds["MEAN-Mbps"],
      inverted: false,
      numeric: true,
    },
    {
      id: "MAX-Mbps",
      label: "MAX-Mbps",
      title: "maximum speed in Mbps during the previous 24 hours",
      renderCell: (value) => (
        <NumberCell
          value={value}
          hasNA={true}
          precision={1}
          thousands={false}
        />
      ),
      numeric: true,
      threshold: thresholds["MAX-Mbps"],
      inverted: false,
      quantiles: metricsQuantileData["MAX-Mbps"],
      cellClassName: quartileColorCellClassName,
      mean: thresholds["MEAN-Mbps"],
    },
    {
      label: "LIM-Mbps",
      id: "LIM-Mbps",
      hidden: true,
    },
    {
      id: "RTT-P80-ms",
      label: "RTT-P80-ms",
      title:
        "The 80-th percentile of all RTT latency, measured from BQN to end user, in 5 minutes.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
      quantiles: metricsQuantileData["RTT-P80-ms"],
      threshold: thresholds["RTT-80%-ms"],
      inverted: true,
      cellClassName: quartileColorCellClassName,
      mean: thresholds["MEAN-Mbps"],
      percent: thresholds["PERCENT-WARNING"],
    },
    {
      id: "RTT-MED-ms",
      label: "RTT-MED-ms",
      title:
        "The 50-th percentile (median) of all RTT latency samples, measured from BQN to end user, in 5 minutes.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
      quantiles: metricsQuantileData["RTT-MED-ms"],
      threshold: thresholds["RTT-MED-ms"],
      inverted: true,
      cellClassName: quartileColorCellClassName,
      mean: thresholds["MEAN-Mbps"],
      percent: thresholds["PERCENT-WARNING"],
    },
    {
      id: "RTT-MIN-ms",
      label: "RTT-MIN-ms",
      title:
        "The mean of all RTT minimum latency samples, measured in 1-second intervals, during active downloads, in 5 minutes.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
      quantiles: metricsQuantileData["RTT-MIN-ms"],
      threshold: thresholds["RTT-MIN-ms"],
      inverted: true,
      cellClassName: quartileColorCellClassName,
      mean: thresholds["MEAN-Mbps"],
      percent: thresholds["PERCENT-WARNING"],
    },
    {
      id: "RTX",
      label: "RTX",
      title:
        "Moving average of the percentage of TCP retransmissions (which reflect packet losses in this direction)",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} percent={true} />
      ),
      numeric: true,
      cellClassName: quartileColorCellClassName,
      quantiles: metricsQuantileData["RTX"],
      threshold: thresholds["RTX"],
      inverted: true,
      mean: thresholds["MEAN-Mbps"],
      percent: thresholds["PERCENT-WARNING"],
    },
    {
      id: "MAX-SPEED",
      label: "MAX-SPEED-%",
      title:
        "Moving average of the percentage of traffic sent with a speed close to the maximum",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} percent={true} />
      ),
      numeric: true,
      cellClassName: quartileColorCellClassName,
      quantiles: metricsQuantileData["MAX-SPEED"],
      threshold: thresholds["MAX-SPEED"],
      inverted: true,
      mean: thresholds["MEAN-Mbps"],
      percent: thresholds["PERCENT-WARNING"],
    },
    {
      id: "CONGEST",
      label: "CONGESTION",
      title: "Moving average of percentage of traffic suffering congestion",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} percent={true} />
      ),
      numeric: true,
      quantiles: metricsQuantileData["CONGEST"],
      threshold: thresholds["CONGEST"],
      inverted: true,
      cellClassName: quartileColorCellClassName,
      mean: thresholds["MEAN-Mbps"],
      percent: thresholds["PERCENT-WARNING"],
    },
    {
      label: "WARN",
      title: "Number of warnings",
      id: "WARN",
      numeric: true,
      renderCell: (value) => (
        <NumberCell
          value={value}
          hasNA={true}
          precision={0}
        />
      ),
      parse: (value) => parseFloat(value),
    },
    {
      label: "LIFETIME",
      id: "LIFETIME",
      title: "Duration so far of this subscriber session",
      numeric: true,
      parse: (data) => (data === "n/a" || !data ? 0 : durationToInt(data)),
    },
    ...(meanSorting !== "default"
      ? [
          {
            label: "MEAN SPEC",
            id: "MEAN-SPEC",
            title: "Mean Value",
            numeric: true,
            hidden: true,
            parse: (value) => parseFloat(value),
          },
        ]
      : []),
  ];
};

function ListSubscribers({
  tableData,
  selected,
  setSelected,
  timePeriod,
  colorize,
  thresholds,
  meanSorting,
  fieldsMeanMap,
  metricsQuantileData
}) {
  const {
    bqnId,
    loginBqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const profile = useProfile();
  const { hideRates = false } = profile || {};
  
  const columns = useMemo(() => {
    return getColumns({
      timePeriod,
      hideRates,
      metricsQuantileData,
      thresholds,
      mode,
      loginBqnId,
      meanSorting
    });
  }, [timePeriod, hideRates, metricsQuantileData, thresholds, mode, loginBqnId, meanSorting]);
  
  const tableClassName = colorize ? "colored" : "notColored";

  const tableDataSpec = useMemo(() => {
    return tableData.map((row) => {
      const meanValue = fieldsMeanMap[row["ADDR"]] !== undefined ? fieldsMeanMap[row["ADDR"]] : fieldsMeanMap[row["SUBSCRIBER-ID"]];
      const calculatedRow = {
        ...row,
        "WARN": getWarningCount(thresholds, row, metricsQuantileData)
      };

      if (meanSorting !== "default") {
        calculatedRow["MEAN-SPEC"] = meanValue !== undefined ? meanValue : "n/a";
      }
      return calculatedRow;
    });
  }, [tableData, fieldsMeanMap, meanSorting, thresholds, metricsQuantileData]);

  const orderByDefault = meanSorting !== "default" ? "MEAN-SPEC" : "";
  const orderDefaultType = meanSorting !== "default" ? "numeric" : "";

  if (profile === null) {
    return null;
  }
  
  return (
    <div className="card attached">
      <TableContainer className={tableClassName}>
        <MuiTable
          isSelectable={true}
          isRowSelectable={false}
          data={tableDataSpec}
          sort={true}
          columns={columns}
          search={true}
          selected={selected}
          setSelected={setSelected}
          csvPrefix="qoe-metrics"
          rowsPerPageNr={25}
          hasExtraSelect={true}
          orderDefault={meanSorting !== "default" ? meanSorting : ""}
          orderByDefault={orderByDefault}
          orderDefaultType={orderDefaultType}
        />
      </TableContainer>
    </div>
  );
}

export default ListSubscribers;
